import Logger from "lib/logger";
import { libStyles } from "lib";
import { getElementsBox } from "lib/elements";
import { canvasMargin } from "views/components/Editor/canvas/canvasConstants";
import ActionBarOps from "views/components/Editor/actionbar/ActionBarOps";

/**
 * Find all caption elements linked to a specific video/audio element
 * @param {Object} elements - All elements in the design
 * @param {string} mediaElementId - The ID of the video/audio element
 * @returns {Array} Array of caption element objects
 */
const findLinkedCaptions = (elements, mediaElementId) => {
  return Object.values(elements).filter(
    el => el.type === "caption" && el.videoElementId === mediaElementId
  );
};

/**
 * Get all element IDs contained within a group (including nested groups)
 * @param {Object} elements - All elements in the design
 * @param {string} groupId - The ID of the group
 * @returns {Array} Array of element IDs in the group
 */
const getGroupElementIds = (elements, groupId) => {
  const group = elements[groupId];
  if (!group || group.type !== "group" || !group.elementsOrder) {
    return [];
  }
  return group.elementsOrder;
};

/**
 * Check if an element or group contains any captions whose parent video is outside
 * @param {Object} elements - All elements in the design
 * @param {string} elementId - The element or group ID being moved
 * @returns {boolean} True if there's an orphaned caption
 */
const hasOrphanedCaption = (elements, elementId) => {
  const element = elements[elementId];
  if (!element) return false;

  // If it's a caption, check if parent is being moved too
  if (element.type === "caption") {
    return true; // Single caption being moved is always orphaned
  }

  // If it's a group, check all elements inside
  if (element.type === "group") {
    const groupElementIds = getGroupElementIds(elements, elementId);

    for (const childId of groupElementIds) {
      const child = elements[childId];
      if (child?.type === "caption") {
        // Check if the caption's parent video is also in this group
        const parentVideoId = child.videoElementId;
        if (!groupElementIds.includes(parentVideoId)) {
          return true; // Caption's parent is not in the group
        }
      }
    }
  }

  return false;
};

/**
 * Returns true if the selection contains a caption whose parent video/audio
 * is not also in the selection (so moving/duplicating would orphan it).
 */
const hasOrphanedCaptionInSelection = selectedElements =>
  selectedElements.some(el => {
    if (el.type !== "caption") return false;
    const parentMediaId = el.videoElementId;
    return !selectedElements.some(
      other => other.uniqueId === parentMediaId || other.id === parentMediaId
    );
  });

/**
 * Find all video/audio elements in a group or return the element if it's a video/audio
 * @param {Object} elements - All elements in the design
 * @param {string} elementId - The element or group ID
 * @returns {Array} Array of video/audio element IDs
 */
const getMediaElementIds = (elements, elementId) => {
  const element = elements[elementId];
  if (!element) return [];

  // If it's a video or audio, return it
  if (element.type === "video" || element.type === "audio") {
    return [elementId];
  }

  // If it's a group, find all video/audio elements inside
  if (element.type === "group") {
    const groupElementIds = getGroupElementIds(elements, elementId);
    return groupElementIds.filter(childId => {
      const child = elements[childId];
      return child?.type === "video" || child?.type === "audio";
    });
  }

  return [];
};

const EditorMoveOps = {
  moveSelection({
    selectedElements,
    left,
    top,
    fromPageId,
    toPageId,
    ...rest
  }) {
    Logger.info("Editor.moveSelection called");
    const { designData, zoom } = this.state;

    if (
      fromPageId !== toPageId &&
      hasOrphanedCaptionInSelection(selectedElements)
    ) {
      this.props.showNotification({
        textComponent: "Captions must be on the same page as the video",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    /* First we need to correct the coordinates from the zoom */
    const topUnzoomed = top / zoom;
    const leftUnzoomed = left / zoom;

    // Flatten group entries into their children so each moved element gets
    // its own propagation entry — Design.moveSelection moves the children
    // (via element.elementsOrder) but leaves the group's own left/top
    // unchanged, so iterating selectedElements directly would enqueue a
    // no-op for the group and never enqueue the children at all.
    const flattenedIds = [];
    selectedElements.forEach(el => {
      if (el.type === "group" && Array.isArray(el.elementsOrder)) {
        el.elementsOrder.forEach(childId => flattenedIds.push(childId));
      } else if (el.uniqueId) {
        flattenedIds.push(el.uniqueId);
      }
    });

    // Snapshot pre-move elements so matchElementInDesign's content fallback
    // resolves correctly on each sibling.
    const originalElements = {};
    flattenedIds.forEach(uniqueId => {
      if (designData.elements[uniqueId]) {
        originalElements[uniqueId] = designData.elements[uniqueId];
      }
    });

    const updatedDesignData = designData.moveSelection({
      selectedElements,
      left: leftUnzoomed,
      top: topUnzoomed,
      fromPageId,
      toPageId,
      ...rest
    });

    // Enqueue propagation for "Update Collection" siblings. Cross-page drags
    // aren't replayed (the geometry handler defensively skips siblings whose
    // copy isn't on the source page), so only enqueue same-page drags. Helper
    // short-circuits on non-collection documents.
    if (fromPageId === toPageId) {
      const propagationData = flattenedIds
        .map(uniqueId => {
          const originalElement = originalElements[uniqueId];
          const movedElement = updatedDesignData.elements?.[uniqueId];
          if (!originalElement || !movedElement || !movedElement.type) {
            return null;
          }
          return {
            uniqueId,
            pageId: fromPageId,
            type: movedElement.type,
            originalElement,
            newGeometry: {
              left: movedElement.left,
              top: movedElement.top
            }
          };
        })
        .filter(Boolean);
      if (propagationData.length) {
        ActionBarOps.enqueueGeometryPropagation({
          designData,
          currentPageId: fromPageId,
          selectedElementsData: propagationData,
          collectionDesigns: this.props.collectionDesigns,
          addActionToPropagationQueue: this.props.addActionToPropagationQueue
        });
      }
    }

    this.updateStateAndSave({
      designData: updatedDesignData,
      isDragging: false,
      selectedItems: selectedElements.map(selectedItem => ({
        itemId: selectedItem.uniqueId,
        groupId: toPageId === fromPageId ? selectedItem.groupId : null,
        pageId: toPageId,
        preview: {}
      }))
    });
  },

  // Alt+drag single element, same page. left/top are target zoomed coords
  // (mirrors Editor.move signature). Original stays put; a duplicate is
  // created at the dropped position. No collection propagation — duplicating
  // is an explicit one-page intent.
  //
  // Group: alt-dragging a grouped child currently detaches the duplicate
  // from its group (see duplicateSelection note). Intentional for this
  // iteration; figma-style "duplicate within the same group" is a follow-up.
  duplicateMove({ id, left, top }) {
    Logger.info("Editor.duplicateMove called");
    const { designData, zoom } = this.state;
    const sourceElement = designData.elements?.[id];
    if (!sourceElement) return;

    // "duplicate" restriction blocks the alt-drag; original stays put.
    // canDrag can't gate this upstream (it doesn't know whether Alt is held
    // at drag-start), so the rejection lands here at drop-time — show a
    // toast so the user doesn't see a silent no-op after a full visual drag.
    if (sourceElement.restrictions?.includes("duplicate")) {
      this.props.showNotification({
        textComponent: "This element can't be duplicated",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    const pageId =
      designData.getElementPageId?.(id) || this.state.currentPageId;

    const deltaX = left / zoom - sourceElement.left;
    const deltaY = top / zoom - sourceElement.top;

    const { designUpdated, newIds } = designData.duplicateElementsAtOffset({
      elements: [
        {
          itemId: id,
          pageId,
          groupId: sourceElement.groupId || null
        }
      ],
      deltaX,
      deltaY,
      toPageId: pageId
    });

    this.updateStateAndSave({
      designData: designUpdated,
      isDragging: false,
      selectedItems: newIds.map(newId => ({
        itemId: newId,
        groupId: null,
        pageId,
        preview: {}
      }))
    });
  },

  // Alt+drag across pages. Original stays on fromPage; duplicate appears on
  // toPage at (left, top) zoomed coords.
  duplicateToPage({ id, left, top, fromPage, toPage }) {
    Logger.info("Editor.duplicateToPage called");
    const { designData, zoom } = this.state;
    const sourceElement = designData.elements?.[id];
    if (!sourceElement) return;

    // Same drop-time guard as duplicateMove (see note there) — toast so the
    // user gets feedback for the deliberate Alt+drag gesture.
    if (sourceElement.restrictions?.includes("duplicate")) {
      this.props.showNotification({
        textComponent: "This element can't be duplicated",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    // Mirrors moveToPage: block cross-page duplication of a caption (or a
    // group containing one) whose parent video isn't coming along — otherwise
    // we'd permanently orphan the caption with a dangling videoElementId.
    if (hasOrphanedCaption(designData.elements, id)) {
      this.props.showNotification({
        textComponent: "Captions must be on the same page as the video",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    const deltaX = left / zoom - sourceElement.left;
    const deltaY = top / zoom - sourceElement.top;

    const { designUpdated, newIds } = designData.duplicateElementsAtOffset({
      elements: [
        {
          itemId: id,
          pageId: fromPage,
          groupId: sourceElement.groupId || null
        }
      ],
      deltaX,
      deltaY,
      toPageId: toPage
    });

    this.updateStateAndSave({
      designData: designUpdated,
      isDragging: false,
      selectedItems: newIds.map(newId => ({
        itemId: newId,
        groupId: null,
        pageId: toPage,
        preview: {}
      }))
    });
  },

  // Alt+drag of a multi-selection (mirrors moveSelection signature). left/top
  // are zoomed-pixel cumulative deltas from drag start; originals stay put;
  // duplicates are created on toPageId at original + delta.
  //
  // Cross-page note: when fromPageId !== toPageId, `delta.y` is the raw visual
  // drop delta spanning whole pages + gutters; we strip out that
  // page-traversal portion via pagesMovedOffset, mirroring
  // Design.moveSelectionAcrossPages, so duplicates land at local coords on
  // the destination page rather than off-canvas.
  //
  // Group note: grouped children Alt+dragged on the same page currently
  // detach from their group (pasteElementsCopyArray defaults
  // isClearingRootGroupIds=true). This is intentional for now to keep the
  // selection bookkeeping simple — figma-style "duplicate within the same
  // group" is a deliberate follow-up.
  duplicateSelection({
    selectedElements,
    left,
    top,
    fromPageId,
    toPageId,
    canvasHeight,
    verticalScrollDiff,
    delta,
    fromPageIndex,
    toPageIndex
  }) {
    Logger.info("Editor.duplicateSelection called");
    const { designData, zoom } = this.state;

    if (!selectedElements || !selectedElements.length) return;

    // Filter out elements with the "duplicate" restriction and duplicate the
    // remaining subset at the drop offset. Restricted originals stay put.
    //
    // Intentional divergence from Cmd+D / context-menu Duplicate. Those paths
    // treat any duplicate-restricted element in the selection as an
    // all-or-nothing block (see Editor.js#copyElementsToClipBoard and
    // Editor.onContextMenu.js), but Alt+drag is a direct manipulation
    // gesture aimed at the drop position — refusing to duplicate the
    // unrestricted majority because one element was locked would be more
    // surprising than the divergence itself. Whole-action no-op only when
    // every element in the selection is restricted (handled with a toast
    // below so the user doesn't see a silent rejection).
    const duplicatableElements = selectedElements.filter(
      el => !el.restrictions || !el.restrictions.includes("duplicate")
    );
    if (!duplicatableElements.length) {
      this.props.showNotification({
        textComponent: "This selection can't be duplicated",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    const targetPageId = toPageId || fromPageId;
    const isCrossPage = fromPageId !== targetPageId;

    if (isCrossPage && hasOrphanedCaptionInSelection(duplicatableElements)) {
      this.props.showNotification({
        textComponent: "Captions must be on the same page as the video",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    const deltaX = left / zoom;
    let deltaY;

    if (
      isCrossPage &&
      delta &&
      canvasHeight != null &&
      fromPageIndex != null &&
      toPageIndex != null
    ) {
      const gutterZoomed = canvasMargin * 2 * zoom;
      const pageAndGutterZoomedSize = canvasHeight + gutterZoomed;
      const numberOfPagesMoved = Math.abs(fromPageIndex - toPageIndex);
      const pagesMovedOffset = pageAndGutterZoomedSize * numberOfPagesMoved;

      const referenceTop = getElementsBox(duplicatableElements).top;
      const directionFactor =
        referenceTop * zoom + verticalScrollDiff + delta.y < 0 ? 1 : -1;
      const moveTotalDistance =
        referenceTop * zoom +
        delta.y +
        verticalScrollDiff +
        pagesMovedOffset * directionFactor;
      deltaY = moveTotalDistance / zoom - referenceTop;
    } else {
      deltaY = top / zoom;
    }

    const sourceItems = duplicatableElements.map(el => ({
      itemId: el.uniqueId,
      pageId: fromPageId,
      groupId: el.groupId || null
    }));

    const { designUpdated, newIds } = designData.duplicateElementsAtOffset({
      elements: sourceItems,
      deltaX,
      deltaY,
      toPageId: targetPageId
    });

    this.updateStateAndSave({
      designData: designUpdated,
      isDragging: false,
      selectedItems: newIds.map(newId => ({
        itemId: newId,
        groupId: null,
        pageId: targetPageId,
        preview: {}
      }))
    });
  },

  moveToPage({ id, left, top, fromPage, toPage }) {
    Logger.info("Editor.moveToPage called");
    const { designData, zoom } = this.state;
    const element = designData.elements[id];

    // Block captions (or groups containing orphaned captions) from being moved independently
    if (hasOrphanedCaption(designData.elements, id)) {
      this.props.showNotification({
        textComponent: "Captions must be on the same page as the video",
        type: "noButton",
        icon: "warning",
        duration: 3000,
        className: libStyles.toasterToastCompact
      });
      return;
    }

    /* First we need to correct the coordinates from the zoom */
    const topUnzoomed = top / zoom;
    const leftUnzoomed = left / zoom;

    // Find all video/audio elements being moved (could be in a group)
    const mediaElementIds = getMediaElementIds(designData.elements, id);

    // Collect all linked captions that are NOT part of the element/group being moved
    const groupElementIds =
      element.type === "group"
        ? getGroupElementIds(designData.elements, id)
        : [];
    const linkedCaptionsToMove = [];

    mediaElementIds.forEach(mediaId => {
      const captions = findLinkedCaptions(designData.elements, mediaId);
      captions.forEach(caption => {
        // Only move captions that are not already part of the group being moved
        if (!groupElementIds.includes(caption.uniqueId)) {
          linkedCaptionsToMove.push(caption);
        }
      });
    });

    // Move the main element
    let updatedDesignData = designData.moveElementToPage({
      elementId: id,
      left: leftUnzoomed,
      top: topUnzoomed,
      fromPage,
      toPage
    });

    // Move any linked captions that are outside the group to the same page
    linkedCaptionsToMove.forEach(caption => {
      // Skip captions that are in a group - they need to move with their group
      if (caption.groupId) {
        return;
      }

      // Get the caption's actual page (should be same as video but let's be safe)
      const captionPage = updatedDesignData.getElementPageId(caption.uniqueId);

      // Only move if caption is on a different page than target
      if (captionPage && captionPage !== toPage) {
        updatedDesignData = updatedDesignData.moveElementToPage({
          elementId: caption.uniqueId,
          left: caption.left, // Keep caption's relative position
          top: caption.top,
          fromPage: captionPage,
          toPage
        });
      }
    });

    this.updateStateAndSave({
      designData: updatedDesignData,
      isDragging: false,
      selectedItems: [
        {
          ...this.state.selectedItem,
          itemId: id,
          pageId: toPage,
          groupId: null
        }
      ]
    });
  }
};

export default EditorMoveOps;
